import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { PortalService } from './portal.service';
import { AuthService } from './auth.service';
import { Usuario } from '../models/usuario.model';
import { AcessosUsuarioService } from './acessos-usuario.service';
import { SessionStorageService } from './session-storage.service';
import { RhNetService } from './rh.service';
import { lastValueFrom } from 'rxjs';
import { AcessoUsuario } from '../models/acesso-usuario.model';
import { Vinculo } from '../models/vinculo.model';
import { DocumentoService } from '../modules/main/services/documentos.service';
import { ConviteStorageService } from './convite-storage.service';

@Injectable({
    providedIn: 'root'
})
export class AppService {

    private acessosUsuario: AcessoUsuario | undefined;
    private vinculo: Vinculo | undefined;

    constructor(private portalService: PortalService,
        private rhnetService: RhNetService,
        private router: Router,
        private authService: AuthService,
        private sessionStorageService: SessionStorageService,
        private acessosUsuarioService: AcessosUsuarioService,
        private messageService: MessageService,
        private documentoService: DocumentoService,
        private conviteStorage: ConviteStorageService) {
    }

    async loadAccessData(): Promise<void> {
        try {
            if (this.authService.isValidToken()) {

                await this.acessosUsuarioService.load();

                // ASSINAGO-729: o hash so existe para convite de signatario EXTERNO (a API so o gera
                // quando a pessoa nao foi encontrada pelo codigo nem pelo e-mail). Ter perfil no
                // AssinaGO nao diz nada sobre o convite — o criterio e "ha hash guardado", e a
                // checagem de CPF no aceite continua sendo a trava.
                const hashConvite = this.conviteStorage.obterHashConvite();
                if (hashConvite && !(await this.aceitarConvitePendente(hashConvite))) {
                    this.router.initialNavigation();
                    this.router.navigate(['/acesso-negado']);
                    return;
                }

                if (this.acessosUsuarioService.isNotEmptyRoles()) {
                    // Motivo de recusa que sobrou de um bootstrap anterior sem ser exibido
                    // (aba fechada, chunk que nao carregou) nao pode aparecer numa negacao
                    // futura que nada tem a ver com convite.
                    this.conviteStorage.descartarMotivoRecusa();
                    await this.loadAppData();
                    this.router.initialNavigation();
                } else {
                    this.router.initialNavigation();
                    this.router.navigate(['/acesso-negado']);
                }

            } else {
                // Limpa o storage da sessao OAuth. O contexto do convite vive em
                // localStorage (ConviteStorageService) justamente para nao ser
                // apagado aqui — ver ASSINAGO-702.
                this.sessionStorageService.clear();
                this.router.initialNavigation();
            }
        } catch (error) {
            this.messageService.add({ severity: 'error', summary: 'Erro', detail: 'Erro interno ao buscar dados da aplicação' });
            // Sem isto o app fica sem rota nenhuma: uma falha de load() (rede,
            // 5xx do backend de acessos) reproduz a tela branca permanente
            // que a Task 2 dizia ter eliminado — ver ASSINAGO-702.
            this.router.initialNavigation();
            this.router.navigate(['/erro']);
        }
    }

    /**
     * Aceita o convite guardado e recarrega os acessos (o perfil pode ter acabado de ser concedido).
     * Devolve false quando o aceite falha: o motivo fica guardado para a tela de acesso negado e o
     * contexto do convite e limpo para nao ser retentado no proximo bootstrap deste navegador
     * (ASSINAGO-702).
     */
    private async aceitarConvitePendente(hashConvite: string): Promise<boolean> {
        try {
            const res = await lastValueFrom(this.documentoService.aceitarConvite(hashConvite));
            this.conviteStorage.limpar();
            if (res?.idDocumento) {
                this.conviteStorage.guardar(null, res.idDocumento.toString(), null);
            }
            await this.acessosUsuarioService.load();
            return true;
        } catch (error: any) {
            console.error('Falha ao aceitar convite automaticamente:', error);
            this.conviteStorage.registrarRecusa(error);
            this.conviteStorage.limpar();
            return false;
        }
    }

    async loadAppData(): Promise<void> {
        try {
            if (this.authService.isValidToken()) {
                this.acessosUsuario = await this.findAcessosUsuario();
                this.vinculo = await this.findVinculoUsuario();
                this.sessionStorageService.set('app_acessosUsuario', this.acessosUsuario);
                //this.menuService.loadMenus();

            }
        } catch (error) {
            console.log(error);
            this.messageService.add({ severity: 'error', summary: 'Erro', detail: 'Erro interno ao buscar dados da aplicação' });
        }
    }

    async findAcessosUsuario(): Promise<AcessoUsuario | undefined> {
        if (!this.sessionStorageService.isExist('app_acessos')) {
            return this.portalService.findAcessosUsuario().toPromise();
        } else {
            return this.sessionStorageService.get('app_acessos');
        }
    }

    async findVinculoUsuario(): Promise<Vinculo | undefined> {
        if (!this.sessionStorageService.isExist('app_vinculo')) {

            let vinculo = await lastValueFrom(this.rhnetService.findViculoByCpf())
                .catch(err => {
                    this.messageService.add({ severity: 'error', summary: 'Erro', detail: err.error.detail });
                });
            if (vinculo) {
                return <any>vinculo;
            } else {
                return new Vinculo();
            }
        } else {
            return this.sessionStorageService.get('app_vinculo');
        }
    }

    getVinculo(): Vinculo {
        return (<any>this.vinculo)[0];
    }

    getUsuario(): Usuario {
        return this?.acessosUsuario ? this?.acessosUsuario : (<any>[0])?.usuario;
    }

    getAcessosUsuario(): AcessoUsuario | undefined {
        return this.acessosUsuario;
    }

    isAppDataOk(): boolean {
        // @ts-ignore
        return this.acessosUsuario['perfis'].length > 0;
    }
}
